import React, { useState, useRef, useCallback, useEffect } from "react";

interface ProgressSliderProps {
  value: number; // 0-100
  max?: number;
  onChange?: (value: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  className?: string;
  disabled?: boolean;
  thumb?: React.ReactNode;
  alwaysShowThumb?: boolean;
  height?: number;
  progressColor?: string; // 进度完成色（左侧）
  trackColor?: string; // 进度条底色（右侧）
  trackBorderColor?: string; // 进度条边框颜色
  thumbColor?: string; // 滑动控制器颜色
  thumbBorderColor?: string; // 滑动控制器边框颜色
}

export const ProgressSlider: React.FC<ProgressSliderProps> = ({
  value,
  max = 100,
  onChange,
  onDragStart,
  onDragEnd,
  className = "",
  disabled = false,
  thumb,
  alwaysShowThumb,
  height,
  // 默认颜色值
  trackColor = "rgba(255,255,255,0.5)",
  trackBorderColor = "transparent",
  progressColor = "rgb(255,255,255)",
  thumbColor = "rgb(255,255,255)",
  thumbBorderColor = "rgb(255,255,255)",
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [dragValue, setDragValue] = useState(value);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef(false);

  // 同步外部value到内部dragValue（仅在非拖拽状态下）
  useEffect(() => {
    if (!isDragging) {
      setDragValue(value);
    }
  }, [value, isDragging]);

  const getValueFromPosition = useCallback(
    (clientX: number) => {
      if (!progressBarRef.current) return 0;

      const rect = progressBarRef.current.getBoundingClientRect();
      const percentage = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width)
      );
      return percentage * max;
    },
    [max]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;

      e.preventDefault();
      setIsDragging(true);
      dragStartRef.current = true;
      onDragStart?.();

      const newValue = getValueFromPosition(e.clientX);
      setDragValue(newValue);
      onChange?.(newValue);
    },
    [disabled, getValueFromPosition, onChange, onDragStart]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const newValue = getValueFromPosition(e.clientX);
      setDragValue(newValue);
      onChange?.(newValue);
    },
    [isDragging, getValueFromPosition, onChange]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      dragStartRef.current = false;
      onDragEnd?.();
    }
  }, [isDragging, onDragEnd]);

  // 全局鼠标事件监听
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const progressPercentage = (dragValue / max) * 100;

  return (
    <div
      className={`relative flex items-center w-full h-4 cursor-pointer ${className}`}
      ref={progressBarRef}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 进度条背景 */}
      <div
        className="w-full h-1.5 rounded-lg"
        style={{
          height: height && `${height}px`,
          backgroundColor: trackColor,
          border:
            trackBorderColor !== "transparent"
              ? `1px solid ${trackBorderColor}`
              : "none",
        }}
      >
        {/* 进度条填充 */}
        <div
          className="h-full rounded-lg"
          style={{
            width: `${progressPercentage}%`,
            backgroundColor: progressColor,
          }}
        />
      </div>

      {/* 拖拽控件 */}
      {(alwaysShowThumb || isHovered || isDragging) && (
        <>
          {thumb ? (
            <div
              className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2"
              style={{ left: `${progressPercentage}%` }}
            >
              {thumb}
            </div>
          ) : (
            <div
              className={`absolute top-1/2 w-3 h-3 rounded-full transform -translate-y-1/2 -translate-x-1/2 transition-transform duration-150 ${
                isDragging ? "scale-110" : "scale-100"
              }`}
              style={{
                left: `${progressPercentage}%`,
                backgroundColor: thumbColor,
                border:
                  thumbBorderColor !== "transparent"
                    ? `1px solid ${thumbBorderColor}`
                    : "none",
                boxShadow:
                  thumbBorderColor !== "transparent"
                    ? `0 0 0 1px ${thumbBorderColor}, 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)`
                    : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              }}
            />
          )}
        </>
      )}
    </div>
  );
};
